import React from "react";
import { Box, Button, Skeleton, Typography } from "@mui/material";

export type OkTruthState<T = number> = {
  status: "ok";
  value: T;
  of: number;
  as_of: string;
};

export type NonOkTruthState<T = number> =
  | { status: "loading" }
  // PKT-0441 F4-adjacent · `last_known?: { value, as_of }` was declared here and
  // read by nothing. A type that advertises a capability the renderer does not
  // have is an invitation to set it and assume it shows. Whether a stale value
  // should be surfaced during a failed read is a product decision, not a
  // worker's — removed rather than half-implemented.
  | {
      status: "unknown";
      source: string;
      reason: string;
    }
  | {
      status: "unreported";
      source: string;
      blocked_on: string;
      since: string;
    }
  | {
      status: "disagree";
      a: { value: T; source: string };
      b: { value: T; source: string };
    };

export type TruthState<T = number> = OkTruthState<T> | NonOkTruthState<T>;
export type NonGreenTruthTone = "neutral" | "amber" | "grey" | "red";
export type TruthTone = NonGreenTruthTone | "success";

/**
 * PKT-0441 F4 · `NaN`/`Infinity` are `typeof "number"`. An `ok` state carrying
 * one is not a measurement, so it must never be painted as one — not green, and
 * not as a confident figure. This is the runtime half of the compile-time
 * false-green guard: RP-1 makes an ABSENT value unrepresentable, this makes a
 * NONSENSE value unrenderable-as-healthy.
 */
export function isRenderableTruthValue(value: unknown): boolean {
  return typeof value !== "number" || Number.isFinite(value);
}

function nonFiniteOk<T>(state: TruthState<T>): boolean {
  return (
    state.status === "ok" &&
    (!isRenderableTruthValue(state.value) || !Number.isFinite(state.of))
  );
}

export function severityColour<T>(
  state: OkTruthState<T>,
  greenWhen: (value: T) => boolean,
): TruthTone;
export function severityColour<T>(state: TruthState<T>): NonGreenTruthTone;
export function severityColour<T>(
  state: TruthState<T>,
  greenWhen?: (value: T) => boolean,
): TruthTone {
  // A non-finite "ok" is a failed measurement wearing a success costume.
  if (nonFiniteOk(state)) return "amber";
  if (state.status === "unknown") return "amber";
  if (state.status === "unreported") return "grey";
  if (state.status === "disagree") return "red";
  if (state.status === "ok" && greenWhen?.(state.value)) return "success";
  return "neutral";
}

type SharedTruthProps = {
  label?: string;
  onRetry?: () => void;
  showPercent?: boolean;
};

type TruthProps<T> = SharedTruthProps & {
  state: TruthState<T>;
  greenWhen?: never;
};

/**
 * PKT-0441 · DORMANT API. `greenWhen` has ZERO live callers as of this commit —
 * every tile in `OperationalBand` uses the non-green overload, so no tile on the
 * live page can be `success` today. RP-1's compile-time false-green guard is
 * therefore protecting an API nobody calls: it is CORRECT, but it is not active
 * coverage of anything currently rendered. Do not read a green RP-1 as evidence
 * that the live page is guarded — the live page simply has no green path yet.
 * The guard earns its keep in Phase 2+, when the first real caller appears.
 *
 * Note also what the type system cannot do: it prevents ABSENT data from going
 * green, but it cannot police a WRONG predicate over real data —
 * `greenWhen={() => true}` would paint a genuine zero green. The honest body
 * text ("0 of 202 — counted, not missing") is what saves that case.
 */
type GreenTruthProps<T> = SharedTruthProps & {
  state: OkTruthState<T>;
  greenWhen: (value: T) => boolean;
};

export default function Truth<T>(props: GreenTruthProps<T>): React.ReactElement;
export default function Truth<T>(props: TruthProps<T>): React.ReactElement;
export default function Truth<T>({
  label,
  state: rawState,
  greenWhen,
  onRetry,
  showPercent,
}: SharedTruthProps & {
  state: TruthState<T>;
  greenWhen?: (value: T) => boolean;
}): React.ReactElement {
  // PKT-0441 F4 · last line of defence. A caller that hand-builds an `ok` state
  // around NaN/Infinity (or divides by one) gets an honest "couldn't read",
  // never "NaN of 202 (NaN%)" in confident type. `greenWhen` is not consulted
  // for a degraded state, so no predicate can green it either.
  const state: TruthState<T> = nonFiniteOk(rawState)
    ? {
        status: "unknown",
        source: "this measurement",
        reason: "the value was not a real number",
      }
    : rawState;

  const tone = state.status === "ok" && greenWhen
    ? severityColour(state, greenWhen)
    : severityColour(state);
  const border = {
    loading: { width: "1px", style: "solid", colour: "#bdbdbd" },
    ok: { width: "1px", style: "solid", colour: tone === "success" ? "#2e7d32" : "#bdbdbd" },
    unknown: { width: "2px", style: "solid", colour: "#ed6c02" },
    unreported: { width: "2px", style: "dashed", colour: "#9e9e9e" },
    disagree: { width: "2px", style: "solid", colour: "#d32f2f" },
  }[state.status];

  let body: React.ReactNode;
  if (state.status === "loading") {
    body = (
      <>
        <Skeleton aria-hidden="true" animation="wave" variant="text" width="4ch" />
        <Typography variant="caption" color="text.secondary">Counting…</Typography>
      </>
    );
  } else if (state.status === "ok") {
    const value = String(state.value);
    body = (
      <Typography variant="h5" fontWeight={700} sx={{ fontVariantNumeric: "tabular-nums" }}>
        {value === "0"
          ? `0 of ${state.of} — counted, not missing`
          : `${value} of ${state.of}${showPercent && typeof state.value === "number" && state.of > 0
            ? ` (${Math.round((state.value / state.of) * 100)}%)`
            : ""}`}
      </Typography>
    );
  } else if (state.status === "unknown") {
    body = (
      <>
        <Typography variant="h5" fontWeight={700}>—</Typography>
        <Typography variant="caption" color="text.secondary">
          Couldn't read {state.source}. {state.reason}
        </Typography>
        <Button size="small" onClick={onRetry}>Retry</Button>
      </>
    );
  } else if (state.status === "unreported") {
    body = (
      <>
        <Typography variant="h5" fontWeight={700}>—</Typography>
        <Typography variant="caption" color="text.secondary">
          {state.source} doesn't report this yet · blocked on {state.blocked_on}
        </Typography>
      </>
    );
  } else {
    body = (
      <>
        <Typography variant="h5" fontWeight={700}>
          {String(state.a.value)} / {String(state.b.value)}?
        </Typography>
        <Typography variant="caption" color="text.secondary">
          two sources disagree · {state.a.source} / {state.b.source}
        </Typography>
      </>
    );
  }

  return (
    <Box
      data-severity={tone}
      data-truth-status={state.status}
      data-truth-tone={tone}
      style={{
        borderStyle: border.style,
        borderWidth: border.width,
        borderColor: border.colour,
      }}
      sx={{ borderRadius: 2, p: 1.5, height: "100%" }}
    >
      {label && (
        <Typography variant="caption" color="text.secondary" fontWeight={700}>
          {label}
        </Typography>
      )}
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 0.25 }}>
        {body}
      </Box>
    </Box>
  );
}
